import { useEffect } from "react";
import {
  Settings,
  MessageCircleIcon,
  UsersRoundIcon,
  Bell,
} from "lucide-react";

import { Button } from "@primitives/button";

import { cn } from "@lib/utils";
import AccountMenu from "./AccountMenu";
import { useNavigationState, ActiveNavMenu } from "@/zustand/NavigationState";
import { Badge } from "@/components/primitives/badge";
import RingPulse from "@/components/shared/RingPulse";
import { useCallState } from "@/zustand/CallState";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { fetchUnreadCount } from "@/indexedDB/services/chats";
import { usePublicGroupUsersRequests } from "@/middleware/hooks/usePublicGroupUsersRequests";
import { FLAG_AI_CHAT_ENABLED, ARCHITECT_EXTERNAL_URL } from "./aiChatConfig";
import { ArchitectMark } from "@/components/shared/icons/ArchitectMark";
import BfnCalculatorNavButton from "./BfnCalculatorNavButton";
import useDetectKeyboardOpen from "@/hooks/useDetectKeyboardOpen";
import { IS_IOS } from "@/lib/environmentDetector";

const NAV_BADGE_CLASSNAME =
  "absolute ml-auto h-3 w-6 shrink-0 items-center justify-center p-2 align-middle text-xs right-0 top-0 rounded-lg bg-secondary text-secondary-foreground";

const formatBadgeCount = (n: number) => (n > 9 ? "9+" : n);

export default function NavigationMenu() {
  const setActiveNavMenu = useNavigationState(
    (state) => state.setActiveNavMenu,
  );
  const activeMenuType = useNavigationState((state) => state.activeNavMenu);

  // Enforce the AI chat feature flag against persisted nav state: a user who
  // previously had ActiveNavMenu.AI_CHAT stored could otherwise still load
  // into the in-app AI chat UI when the flag is disabled.
  useEffect(() => {
    if (!FLAG_AI_CHAT_ENABLED && activeMenuType === ActiveNavMenu.AI_CHAT) {
      setActiveNavMenu(ActiveNavMenu.P2P_CHATS);
    }
  }, [activeMenuType, setActiveNavMenu]);
  const { p2pUnreadCount, groupUnreadCount, aiUnreadCount } = useSafeLiveQuery(
    () => fetchUnreadCount(),
    [],
    { p2pUnreadCount: 0, groupUnreadCount: 0, aiUnreadCount: 0 },
  );

  const hasActiveCallInP2Ps = useCallState(
    (state) => state.hasActiveCallInP2Ps,
  );
  const hasActiveCallInGroups = useCallState(
    (state) => state.hasActiveCallInGroups,
  );
  const showUnreadMessagesInP2P = p2pUnreadCount > 0;
  const showUnreadMessagesInGroups = groupUnreadCount > 0;
  const showUnreadMessagesInAI = FLAG_AI_CHAT_ENABLED && aiUnreadCount > 0;
  const { requestCount } = usePublicGroupUsersRequests();

  // The mobile nav is fixed to the LAYOUT viewport bottom, which iOS puts
  // BEHIND the on-screen keyboard — the iOS keyboard is slightly
  // translucent, so the pill showed through as a ghost silhouette
  // (Catalin, real device, 08-04). Hide it while the keyboard is up;
  // desktop (lg:) keeps its sidebar regardless.
  const isKeyboardOpen = useDetectKeyboardOpen({ delay: 0 });
  const hideForKeyboard = isKeyboardOpen && IS_IOS;

  // Hand the hidden bar's space to the content: `mobileLeftColumnHeight`
  // subtracts `--bottom-nav-space` (default: the 64px band + home-indicator
  // inset), so zeroing it lets the chat list / search views run all the way
  // down to the keyboard. Without this, the reserved band became a dead gap
  // that whatever sat behind the column bled into (Catalin, real device,
  // 08-04 round 2).
  useEffect(() => {
    const root = document.documentElement;
    if (hideForKeyboard) {
      root.style.setProperty("--bottom-nav-space", "0px");
    } else {
      root.style.removeProperty("--bottom-nav-space");
    }
    return () => {
      root.style.removeProperty("--bottom-nav-space");
    };
  }, [hideForKeyboard]);

  return (
    <div
      className={cn(
        "fixed bottom-0 z-40 flex h-mobileNavigationMenuHeight w-full flex-row items-center justify-between border-primary/20 bg-card pb-[env(safe-area-inset-bottom,0px)] lg:relative lg:flex lg:h-full lg:w-navigationMenuWidth lg:shrink-0 lg:flex-col lg:border-r lg:pb-0",
        hideForKeyboard && "hidden lg:flex",
      )}
    >
      <div className="hidden h-navigationMenuHeaderHeight items-center justify-center align-middle lg:flex">
        <img
          src="/orion-logo.svg"
          alt="ORION"
          className="size-12 dark:invert"
        />
      </div>

      <div className="mx-auto w-full px-5 lg:my-auto lg:flex-1 lg:px-0">
        <ul className="flex w-full flex-row items-center justify-center gap-0 rounded-full bg-muted py-2 align-middle shadow-md lg:flex lg:h-full lg:flex-col lg:gap-3 lg:rounded-none lg:bg-transparent lg:py-7 lg:shadow-none">
          <li className="grow lg:grow-0">
            <Button
              onClick={() => {
                if (FLAG_AI_CHAT_ENABLED) {
                  setActiveNavMenu(ActiveNavMenu.AI_CHAT);
                } else {
                  window.open(
                    ARCHITECT_EXTERNAL_URL,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              }}
              size="icon"
              variant="ghost"
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl hover:bg-transparent",
                FLAG_AI_CHAT_ENABLED &&
                  activeMenuType === ActiveNavMenu.AI_CHAT &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              {FLAG_AI_CHAT_ENABLED ? (
                <ArchitectMark className="size-7" />
              ) : (
                <img
                  src="/icons/old-logo-architect.svg"
                  alt="ORION"
                  className="size-7 invert dark:invert-0"
                />
              )}
              {showUnreadMessagesInAI && (
                <Badge variant="secondary" className={NAV_BADGE_CLASSNAME}>
                  {formatBadgeCount(aiUnreadCount)}
                </Badge>
              )}
            </Button>
          </li>

          {/* BFN Calculator — sits directly under OA (second in line).
              Renders nothing while the flag is off (default). */}
          <BfnCalculatorNavButton />

          <li className="grow lg:grow-0">
            <Button
              onClick={() => {
                setActiveNavMenu(ActiveNavMenu.NOTIFICATIONS);
              }}
              size="icon"
              variant="ghost"
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl hover:bg-transparent",
                activeMenuType === ActiveNavMenu.NOTIFICATIONS &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              <Bell size={24} strokeWidth={1.5} />

              {requestCount > 0 && (
                <Badge variant="secondary" className={NAV_BADGE_CLASSNAME}>
                  {formatBadgeCount(requestCount)}
                </Badge>
              )}
            </Button>
          </li>
          <li className="grow lg:grow-0">
            <Button
              onClick={() => {
                setActiveNavMenu(ActiveNavMenu.P2P_CHATS);
              }}
              size="icon"
              variant="ghost"
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl hover:bg-transparent",
                activeMenuType === ActiveNavMenu.P2P_CHATS &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              <MessageCircleIcon size={24} strokeWidth={1.5} />
              {showUnreadMessagesInP2P && (
                <Badge variant="secondary" className={NAV_BADGE_CLASSNAME}>
                  {formatBadgeCount(p2pUnreadCount)}
                </Badge>
              )}
              {hasActiveCallInP2Ps && (
                <div className="absolute bottom-0.5 right-0.5 size-2 rounded-full bg-secondary"></div>
              )}
              <RingPulse
                active={hasActiveCallInP2Ps}
                className="!bottom-0 !left-auto !right-0 !top-auto size-3 bg-secondary"
              />
            </Button>
          </li>

          <li className="grow lg:grow-0">
            <Button
              onClick={() => {
                setActiveNavMenu(ActiveNavMenu.GROUP_CHATS);
              }}
              size="icon"
              variant="ghost"
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl hover:bg-transparent",
                activeMenuType === ActiveNavMenu.GROUP_CHATS &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              <UsersRoundIcon size={24} strokeWidth={1.5} />
              {showUnreadMessagesInGroups && (
                <Badge variant="secondary" className={NAV_BADGE_CLASSNAME}>
                  {formatBadgeCount(groupUnreadCount)}
                </Badge>
              )}
              {hasActiveCallInGroups && (
                <div className="absolute bottom-0.5 right-0.5 size-2 rounded-full bg-secondary"></div>
              )}
              <RingPulse
                active={hasActiveCallInGroups}
                className="!bottom-0 !left-auto !right-0 !top-auto size-3 bg-secondary"
              />
            </Button>
          </li>

          <li className="hidden lg:mt-auto lg:block">
            <Button
              onClick={() => {
                setActiveNavMenu(ActiveNavMenu.SETTINGS);
              }}
              size="icon"
              variant="ghost"
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl hover:bg-transparent",
                activeMenuType === ActiveNavMenu.SETTINGS &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              <Settings size={24} strokeWidth={1.5} />
            </Button>
          </li>

          <AccountMenu />
        </ul>
        {/* Desktop-only spacer. On mobile the 56px pill must fit the compact
            64px band (56 + this 28px spacer would overflow and clip the pill
            under LeftColumn); the safe-area inset below the band already
            provides the breathing room. */}
        <div className="hidden h-7 w-full lg:block"></div>
      </div>
    </div>
  );
}
